import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
  Badge,
} from "@chakra-ui/react";
import { useColorModeValue } from "../components/ui/color-mode";
import { LinkBox, LinkOverlay } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";

const envApiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const EMPTY_TICKERS = [];
const USD_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

function getDefaultLocalApiUrl() {
  try {
    const host = window?.location?.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://127.0.0.1:8000";
    }
  } catch {
    // ignore
  }
  return "";
}

function formatLastCovered(lastCovered) {
  if (!lastCovered) return "Unknown";
  try {
    // last_covered comes back as "YYYY-MM-DD"
    const d = new Date(`${lastCovered}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return "Unknown";
    return d.toLocaleDateString();
  } catch {
    return String(lastCovered);
  }
}

function formatUsd(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  try {
    return USD_FORMATTER.format(n);
  } catch {
    return `$${n}`;
  }
}

function StockCard({
  title,
  row,
  onOpen,
  becauseTickers = EMPTY_TICKERS,
  action = "button", // "button" | "icon" | "none"
}) {
  const cardBg = useColorModeValue("rgba(255,255,255,0.7)", "rgba(0,0,0,0.35)");
  const border = useColorModeValue("gray.200", "gray.700");
  const muted = useColorModeValue("gray.600", "gray.300");
  const hoverBorder = useColorModeValue("teal.400", "teal.300");

  if (!row) return null;

  const sector = row.nasdaq_sector || row.gics_sector || "";
  const industry = row.nasdaq_industry || row.gics_industry || "";
  const becauseText =
    becauseTickers && becauseTickers.length > 0 ? `Because you covered: ${becauseTickers.join(", ")}` : "";

  const effectiveTitle =
    title && String(title).trim() && String(title).toUpperCase() !== String(row.ticker || "").toUpperCase()
      ? title
      : "";

  const isClickableCard = Boolean(onOpen) && action !== "none";

  return (
    <Box
      as="article"
      w="100%"
      borderWidth="1px"
      borderColor={border}
      borderRadius="lg"
      bg={cardBg}
      p={4}
      position="relative"
      transition="border-color 0.15s ease, transform 0.15s ease"
      cursor={isClickableCard ? "pointer" : "default"}
      role={isClickableCard ? "link" : undefined}
      tabIndex={isClickableCard ? 0 : undefined}
      onClick={isClickableCard ? onOpen : undefined}
      onKeyDown={
        isClickableCard
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
      _hover={
        isClickableCard
          ? {
              borderColor: hoverBorder,
              transform: "translateY(-1px)",
            }
          : undefined
      }
    >
      {action === "icon" && isClickableCard && (
        <Box
          as="button"
          type="button"
          aria-label="Open stock"
          onClick={(e) => {
            e.stopPropagation();
            onOpen?.();
          }}
          position="absolute"
          top={2}
          right={2}
          zIndex={2}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="36px"
          h="36px"
          borderRadius="md"
          color={muted}
          bg="transparent"
          _hover={{}}
          border="none"
          outline="none"
        >
          <Box as={FiExternalLink} />
        </Box>
      )}
      <Flex justify="space-between" align="center" gap={3} wrap="wrap">
        <Box>
          {!!effectiveTitle && (
            <Text fontSize="sm" color={muted}>
              {effectiveTitle}
            </Text>
          )}
          <Text fontSize="2xl" fontWeight="bold">
            {row.ticker}
          </Text>
          <br />
          <Text fontSize="sm" color={muted} mt={2}>
            {row.name || ""}
          </Text>
          {!!becauseText && (
            <Text fontSize="sm" color={muted} mt={1} noOfLines={2}>
              {becauseText}
            </Text>
          )}
        </Box>
        {action === "button" && (
          <Button onClick={onOpen} colorScheme="teal">
            Open
          </Button>
        )}
      </Flex>

      <Flex mt={3} gap={4} wrap="wrap">
        <Text fontSize="sm" color={muted}>
          Sector: {sector || "—"}
        </Text>
        <Text fontSize="sm" color={muted}>
          Industry: {industry || "—"}
        </Text>
        <Text fontSize="sm" color={muted}>
          Last covered: {formatLastCovered(row.last_covered)}
        </Text>
        <Text fontSize="sm" color={muted}>
          Payment: {formatUsd(row.current_payment_usd)}
        </Text>
      </Flex>
    </Box>
  );
}

export default function SeekingAlphaSuggest() {
  const [author, setAuthor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const bgStart = useColorModeValue("gray.50", "gray.800");
  const bgEnd = useColorModeValue("teal.200", "teal.900");
  const muted = useColorModeValue("gray.600", "gray.300");
  const panelBg = useColorModeValue("rgba(255,255,255,0.6)", "rgba(0,0,0,0.25)");

  const suggested = result?.suggested || null;
  const alternatives = useMemo(() => result?.alternatives || [], [result]);
  const covered = useMemo(() => result?.covered || [], [result]);
  const coveredItems = useMemo(() => result?.covered_items || [], [result]);
  const suggestionError = result?.suggestion_error || "";
  const whySectors = useMemo(() => result?.debug?.nasdaq_top_sectors || [], [result]);
  const whyIndustries = useMemo(() => result?.debug?.nasdaq_top_industries || [], [result]);
  const tickerMetadata = useMemo(() => result?.ticker_metadata || {}, [result]);

  const becauseTickers = useMemo(() => {
    if (!suggested) return [];
    const s = String(suggested?.ticker || "").toUpperCase();
    return (covered || [])
      .filter((c) => c?.ticker && String(c.ticker).toUpperCase() !== s)
      .slice(0, 3)
      .map((c) => String(c.ticker).toUpperCase());
  }, [covered, suggested]);

  const submit = async () => {
    setError("");
    setResult(null);
    const trimmed = author.trim();
    if (!trimmed) {
      setError("Paste a Seeking Alpha author URL or slug.");
      return;
    }

    setLoading(true);
    try {
      const base = envApiUrl || getDefaultLocalApiUrl();
      const endpoint = base
        ? `${base}/api/seeking-alpha/suggest-next`
        : `/api/seeking-alpha/suggest-next`;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ author: trimmed }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.error || `Request failed (${res.status})`);
        return;
      }
      setResult(json);
    } catch (e) {
      setError("Network error calling the suggestion API.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      minH="calc(100vh - var(--nav-h))"
      direction="column"
      bgGradient="to-b"
      gradientFrom={bgStart}
      gradientTo={bgEnd}
      p={4}
    >
      <VStack spacing={6} w={{ base: "95%", md: "720px" }} mx="auto" mt={6} align="stretch">
        <Box>
          <Text fontSize="3xl" fontWeight="bold">
            Seeking Alpha (beta)
          </Text>
          <Text fontSize="sm" color={muted} mt={1}>
            Paste an author URL or slug (e.g. <code>daniel-jones</code>).
          </Text>
        </Box>

        <Flex gap={3} wrap="wrap">
          <Input
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="https://seekingalpha.com/author/daniel-jones"
            bg="white"
            borderRadius="lg"
            flex="1"
            minW="260px"
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
          />
          <Button onClick={submit} colorScheme="teal" disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Suggest a stock"}
          </Button>
        </Flex>

        {error && (
          <Box borderWidth="1px" borderColor="red.300" borderRadius="lg" p={3} bg="rgba(255,0,0,0.06)">
            <Text color="red.600" _dark={{ color: "red.200" }} fontSize="sm">
              {error}
            </Text>
          </Box>
        )}

        {/* Two panels */}
        <Flex gap={4} wrap="wrap" align="flex-start">
          <Box flex="1" minW={{ base: "100%", md: "340px" }}>
            <Text fontSize="lg" fontWeight="semibold" mb={2}>
              Previously covered (from author RSS)
            </Text>
            <Box
              borderWidth="1px"
              borderRadius="lg"
              p={4}
              bg={panelBg}
            >
              {covered.length === 0 ? (
                <Text fontSize="sm" color={muted}>
                  Run a suggestion to see tickers extracted from the author feed.
                </Text>
              ) : (
                <>
                  <Wrap spacing={2}>
                    {covered.slice(0, 30).map((c) => (
                      <WrapItem key={c.ticker}>
                        <Badge
                          colorScheme="purple"
                          cursor="pointer"
                          onClick={() => navigate(`/stock/${String(c.ticker || "").toLowerCase()}`)}
                        >
                          {c.ticker}
                          {c.count ? ` (${c.count})` : ""}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                  {coveredItems?.length > 0 && (
                    <Box mt={4}>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>
                        Sample feed items
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {coveredItems
                          .filter((it) => (it?.tickers || []).length > 0)
                          .slice(0, 5)
                          .map((it, idx) => {
                            const firstTicker = (it.tickers || [])[0]?.toUpperCase();
                            const meta = firstTicker ? tickerMetadata[firstTicker] : null;
                            const sector = meta?.nasdaq_sector || "";
                            const industry = meta?.nasdaq_industry || "";
                            return (
                              <LinkBox
                                key={`${it.link || idx}`}
                                as="article"
                                borderWidth="1px"
                                borderRadius="md"
                                p={2}
                                cursor={it?.link ? "pointer" : "default"}
                                _hover={it?.link ? { borderColor: "teal.400" } : undefined}
                              >
                                <Text fontSize="md" color={muted} noOfLines={1}>
                                  {(it.tickers || []).join(", ")}
                                </Text>
                                {it?.link ? (
                                  <LinkOverlay href={it.link} isExternal>
                                    <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                                      {it.title || "Untitled"}
                                    </Text>
                                  </LinkOverlay>
                                ) : (
                                  <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                                    {it.title || "Untitled"}
                                  </Text>
                                )}
                                
                                {(sector || industry) && (
                                  <Text fontSize="xs" color={muted} noOfLines={1}>
                                    {[sector, industry].filter(Boolean).join(" · ")}
                                  </Text>
                                )}
                              </LinkBox>
                            );
                          })}
                      </VStack>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>

          <Box flex="1" minW={{ base: "100%", md: "340px" }}>
            <Text fontSize="lg" fontWeight="semibold" mb={2}>
              Suggested stocks
            </Text>
            {(whySectors.length > 0 || whyIndustries.length > 0) && (
              <Box mb={3}>
                <Text fontSize="sm" color={muted}>
                  Suggested because you cover:
                  {whySectors.length > 0 ? ` sectors (${whySectors.join(", ")})` : ""}
                  {whyIndustries.length > 0 ? ` and industries (${whyIndustries.slice(0, 6).join(", ")})` : ""}
                  .
                </Text>
              </Box>
            )}
            {!suggested ? (
              <Box
                borderWidth="1px"
                borderRadius="lg"
                p={4}
                bg={panelBg}
              >
                {!!suggestionError && (
                  <Text fontSize="sm" color={muted} mb={2}>
                    {suggestionError}
                  </Text>
                )}
                <Text fontSize="sm" color={muted}>
                  Suggestions will appear here after you run the search.
                </Text>
                <Text fontSize="xs" color={muted} mt={2}>
                  Sector/Industry matching is not enabled yet (Nasdaq enrichment needed).
                </Text>
              </Box>
            ) : (
              <VStack spacing={3} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Top suggestion
                  </Text>
                  <StockCard
                    row={suggested}
                    becauseTickers={becauseTickers}
                    onOpen={() => navigate(`/stock/${String(suggested.ticker || "").toLowerCase()}`)}
                  />
                </Box>
                {alternatives.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      Alternatives
                    </Text>
                    <VStack spacing={3} align="stretch">
                      {alternatives.map((row) => (
                        <StockCard
                          key={row.ticker}
                          row={row}
                          onOpen={() => navigate(`/stock/${String(row.ticker || "").toLowerCase()}`)}
                          action="icon"
                        />
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}
          </Box>
        </Flex>
      </VStack>
    </Flex>
  );
}
